import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { FaFileAlt, FaUpload, FaTrash } from 'react-icons/fa';
import { API_URL } from '../../config/api';
import { 
  createProjectItemDocument, 
  deleteProjectItemDocument 
} from '../../api/documents';
import { DocumentUploadModal } from '../DocumentUploadModal';

interface TabProjectPlanDocumentAttachmentProps {
  projectId: string;
  itemId: string;
  documentUrl?: string;
  fileName?: string;
}

interface DocumentData {
  id: string;
  name: string;
  file_name?: string;
  size: number;
  file_size?: number;
  mimetype?: string;
  content_type?: string;
  uploadedBy: string;
  uploadedAt: string;
  created_at?: string;
  updated_at?: string;
  url?: string;
  description?: string;
  document_kind?: string;
  version?: number;
}

const TabProjectPlanDocumentAttachmentComponent = ({ 
  projectId, 
  itemId,
  documentUrl,
  fileName
}: TabProjectPlanDocumentAttachmentProps) => {
  const { keycloak: { token } } = useKeycloak();
  const [documentData, setDocumentData] = useState<DocumentData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Load document on component mount
  useEffect(() => {
    if (documentUrl && fileName) {
      const propDoc: DocumentData = {
        id: `${projectId}-${itemId}`,
        name: fileName,
        file_name: fileName,
        size: 0,
        mimetype: 'application/octet-stream',
        uploadedBy: 'unknown',
        uploadedAt: new Date().toISOString(),
        url: documentUrl
      };
      setDocumentData(propDoc);
      return;
    }
    
    if (token && projectId && itemId) {
      loadDocument();
    }
  }, [token, projectId, itemId, documentUrl, fileName]);

  const loadDocument = async () => {
    if (!token || !projectId || !itemId) return;

    try {
      setIsLoading(true);
      console.log(`Loading document for project ${projectId}, item ${itemId}`);
      
      const response = await fetch(`${API_URL}/projects/${projectId}/items/${itemId}/document`, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer ' + token,
          'content-type': 'application/json'
        }
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          // Got JSON metadata
          const data = await response.json();
          console.log('Document metadata loaded:', data);
          
          const normalizedData: DocumentData = {
            id: data.id || `${projectId}-${itemId}`,
            name: data.file_name || data.name || 'document',
            file_name: data.file_name || data.name || 'document',
            size: data.file_size || data.size || 0,
            mimetype: data.content_type || data.mimetype,
            uploadedBy: data.uploadedBy || 'unknown',
            uploadedAt: data.created_at || data.uploadedAt || new Date().toISOString(),
            url: data.url,
            description: data.description,
            document_kind: data.document_kind,
            version: data.version
          };
          
          setDocumentData(normalizedData);
        } else {
          // Got file content - create minimal document data
          const contentDisposition = response.headers.get('content-disposition');
          let filename = 'document';
          
          if (contentDisposition) {
            const match = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
            if (match) {
              filename = match[1].replace(/['"]/g, '').trim();
            }
            }
          
          const minimalDoc: DocumentData = {
            id: `${projectId}-${itemId}`,
            name: filename,
            size: 0,
            mimetype: contentType || 'application/octet-stream',
            uploadedBy: 'unknown',
            uploadedAt: new Date().toISOString(),
            url: `${API_URL}/projects/${projectId}/items/${itemId}/document`
          };
          
          setDocumentData(minimalDoc);
        }
      } else if (response.status === 404) {
        console.log('No document found for this item');
        setDocumentData(null);
      } else {
        console.error('Failed to load document:', response.status, response.statusText);
        setDocumentData(null);
      }
    } catch (error) {
      console.error('Error loading document:', error);
      setDocumentData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadFromModal = async (file: File) => {
    if (!token) return;

    try {
      console.log('Uploading project plan file:', file.name, 'Size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      
      const response = await createProjectItemDocument(token, projectId, itemId, file);
      console.log('Upload response:', response);
      console.log('Upload response URL:', response.url);

      // Normalize the response data
      const normalizedDoc: DocumentData = {
        id: response.id || `${projectId}-${itemId}`,
        name: response.name || file.name,
        file_name: response.name || file.name,
        size: response.size || file.size,
        mimetype: response.mimetype || file.type,
        uploadedBy: response.uploadedBy || 'current-user',
        uploadedAt: response.uploadedAt || new Date().toISOString(),
        url: response.url, // This is the key - use the URL from API response!
      };

      console.log('Normalized document data:', normalizedDoc);
      console.log('Document URL for download:', normalizedDoc.url);
      
      // Set the document data immediately - no delays, no processing states
      setDocumentData(normalizedDoc);
      toast.success(`Document "${file.name}" uploaded successfully`);
    } catch (error) {
      console.error('Failed to upload project plan document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Handle specific HTTP errors with user-friendly messages
      if (errorMessage.includes('413') || errorMessage.includes('Content Too Large')) {
        const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
        toast.error(`File "${file.name}" (${fileSizeMB}MB) is too large. Maximum size is 10MB. Please try a smaller file.`);
      } else if (errorMessage.includes('415') || errorMessage.includes('Unsupported Media Type')) {
        toast.error('File type not supported. Please try a different file format.');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        toast.error('You do not have permission to upload documents to this project plan item.');
      } else if (errorMessage.includes('404')) {
        toast.error('Project plan item not found. Please refresh the page and try again.');
      } else if (errorMessage.includes('500')) {
        toast.error('Server error occurred. Please try again in a few moments.');
      } else {
        toast.error(`Failed to upload document: ${errorMessage}`);
      }
      
      throw error; // Re-throw to let DocumentUploadModal handle it
    }
  };

  const handleDownload = async () => {
    if (!documentData || !token) {
      console.warn('Cannot download: missing document data or token');
      return;
    }

    try {
      console.log('Downloading document:', {
        id: documentData.id,
        name: documentData.name,
        url: documentData.url,
        mimetype: documentData.mimetype
      });

      // Use URL from document data or construct proper download URL
      let downloadUrl = documentData.url;
      
      // If no URL from API, construct the correct download endpoint with filename
      if (!downloadUrl) {
        const fileName = documentData.file_name || documentData.name || 'document';
        downloadUrl = `${API_URL}/projects/${projectId}/items/${itemId}/document/${encodeURIComponent(fileName)}`;
        console.log('Constructed download URL:', downloadUrl);
      } else {
        console.log('Using API provided URL:', downloadUrl);
      }
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer ' + token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
      
      let filename = documentData.file_name || documentData.name || 'document';
        
        // Check if file can be opened in browser
        const openableTypes = ['pdf', 'image', 'text'];
        const canOpen = documentData.mimetype ? openableTypes.some(type => 
          documentData.mimetype?.toLowerCase().includes(type)
        ) : false;

        if (canOpen) {
          // Open in new tab for viewable files
        console.log('Opening file in new tab');
          window.open(url, '_blank');
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        } else {
          // Force download for other file types
        console.log('Forcing download');
          const a = document.createElement('a');
          a.href = url;
        a.download = filename;
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }

        toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error(`Failed to download document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDelete = async () => {
    if (!documentData || !token || !projectId || !itemId) return;

    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      console.log(`Deleting document from project ${projectId}, item ${itemId}`);
      
      await deleteProjectItemDocument(token, projectId, itemId);
      setDocumentData(null);
      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error(`Failed to delete document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-2">
        <div className="text-xs text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-1">
      {documentData ? (
        // Document exists - show name and actions
        <div className="flex items-center space-x-2 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1">
          <button
            onClick={handleDownload}
            className="cursor-pointer flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800"
            title={`${documentData.name}${documentData.size ? ` (${formatFileSize(documentData.size)})` : ''}`}
          >
            <FaFileAlt className="text-xs" />
            <span className="font-medium" title={documentData.file_name || documentData.name}>
              {(() => {
                const displayName = documentData.file_name || documentData.name || 'document';
                return displayName.length > 7 
                  ? `${displayName.substring(0, 7)}...` 
                  : displayName;
              })()}
            </span>
          </button>
          <button
            onClick={handleDelete}
            className="text-xs text-gray-400 hover:text-red-500 p-1"
            title="Delete document"
          >
            <FaTrash />
          </button>
        </div>
      ) : (
        // No document - show upload button
        <div className="flex items-center">
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center space-x-1 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-3 py-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            title="Upload document"
          >
            <FaUpload className="text-xs" />
            <span>Upload</span>
          </button>
        </div>
      )}
      
      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadFromModal}
        title="Upload Project Plan Document"
        maxFileSize={10}
        description="Select a document or drag and drop it here"
      />
    </div>
  );
}; 

export const TabProjectPlanDocumentAttachment = (props: TabProjectPlanDocumentAttachmentProps) => (
    <TabProjectPlanDocumentAttachmentComponent {...props} />
); 